import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography,
} from '@mui/material';
import { Check, Sparkles } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useInRouterContext } from 'react-router-dom';
import { api, orThrow } from '../../lib/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { AiUpsellContext } from './aiUpsellContext';

/**
 * "AI not in your plan" upsell (medopd-docs 126). When the clinic's plan has no AI_ASSISTANT module, AI buttons
 * stay on screen; clicking one opens this dialog instead of failing after the click. Clinic and group admins can
 * ask ClinVea for the add-on from here; everyone else is told to ask their admin.
 *
 * A feature the clinic admin switched off in Admin → AI settings is NOT upsold — that's the clinic's choice
 * (see AiFeatureGate). Role/permission gating is unchanged: a user who may not use a feature still never sees it.
 */

/** Roles that can ask ClinVea for the add-on (POST /api/clinic/addon-requests). */
const REQUESTER_ROLES = new Set(['clinic_admin', 'group_admin']);

const STATUS_KEY = 'ai-addon-request-status';

const BENEFITS = ['benefitScribe', 'benefitLab', 'benefitSummary', 'benefitBilling', 'benefitChat'] as const;

/**
 * The upsell body: what AI does, where to get it, and the role-specific action. Shared by the dialog and the
 * in-page panel shown on AI-only routes.
 */
export function AiUpsellContent({ featureLabel, onClose, variant = 'dialog' }: {
  featureLabel?: string;
  onClose?: () => void;
  variant?: 'dialog' | 'panel';
}) {
  const { t } = useTranslation('common');
  const { t: ta } = useTranslation('admin');
  const inRouter = useInRouterContext();
  const { role } = usePermissions();
  const { activeClinicId } = useTenant();
  const qc = useQueryClient();
  const canRequest = !!role && REQUESTER_ROLES.has(role);

  const status = useQuery({
    queryKey: [STATUS_KEY, activeClinicId],
    queryFn: async () => {
      const res = await api.addonRequests.status('AI_ASSISTANT');
      if (res.error) throw new Error(res.error);
      return res.data ?? null;
    },
    enabled: canRequest,
    staleTime: 60_000,
    retry: false,
  });

  const [conflict, setConflict] = useState(false);
  const request = useMutation({
    mutationFn: () => orThrow(api.addonRequests.request('AI_ASSISTANT')),
    onSuccess: () => {
      qc.setQueryData([STATUS_KEY, activeClinicId], (prev: unknown) => ({
        ...(prev as object | null ?? {}), addon: 'AI_ASSISTANT', moduleEnabled: false, requestedToday: true,
      }));
    },
    onError: (e: unknown) => {
      // 409: the module is on already (the permissions cache is behind). Refresh it so the buttons start working.
      const msg = e instanceof Error ? e.message : '';
      if (/already has/i.test(msg)) {
        setConflict(true);
        qc.invalidateQueries({ queryKey: ['my-permissions'] });
      }
    },
  });

  const requestedToday = !!status.data?.requestedToday;
  const justSent = request.isSuccess && !request.data?.alreadyRequested;

  const action = canRequest ? (
    <Stack spacing={1} alignItems={variant === 'panel' ? 'flex-start' : 'stretch'}>
      {justSent || requestedToday ? (
        <Typography role="status" variant="body2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Check size={16} />
          {justSent ? t('aiUpsell.requestSent') : t('aiUpsell.alreadyRequested')}
        </Typography>
      ) : (
        <Button
          variant="contained"
          startIcon={<Sparkles size={16} />}
          disabled={request.isPending || status.isLoading}
          onClick={() => request.mutate()}
        >
          {request.isPending ? t('aiUpsell.requesting') : t('aiUpsell.requestAddon')}
        </Button>
      )}
      {request.isError && (
        <Typography role="alert" variant="body2" color="error">
          {conflict ? t('aiUpsell.alreadyEnabled') : t('aiUpsell.requestFailed')}
        </Typography>
      )}
    </Stack>
  ) : (
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('aiUpsell.askAdmin')}</Typography>
  );

  return (
    <Box>
      {featureLabel && (
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {t('aiUpsell.featureIsAi', { feature: featureLabel })}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('aiUpsell.intro')}</Typography>
      <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5, '& li': { mb: 0.5 } }}>
        {BENEFITS.map(k => (
          <Typography key={k} component="li" variant="body2">{t(`aiUpsell.${k}`)}</Typography>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>{t('aiUpsell.plan')}</Typography>
      {action}
      {canRequest && (
        // Plan, limits and every add-on in one place (medopd-docs 136 §3 H5).
        <Button {...(inRouter ? { component: RouterLink, to: '/admin/plan' } : { href: '/admin/plan' })} size="small" sx={{ mt: 1 }} onClick={onClose}>
          {ta('yourPlan.seeYourPlan')}
        </Button>
      )}
      {variant === 'panel' && onClose && (
        <Button sx={{ mt: 1 }} onClick={onClose}>{t('aiUpsell.close')}</Button>
      )}
    </Box>
  );
}

/** Full-page version for AI-only routes (Lab AI Review, Knowledge Base, AI Settings, Consultation recorder). */
export function AiUpsellPanel({ featureLabel }: { featureLabel?: string }) {
  const { t } = useTranslation('common');
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 2 }, py: { xs: 2, sm: 4 } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 560, width: '100%', borderRadius: 3 }} data-testid="ai-upsell-panel">
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}><Sparkles size={22} /></Box>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>{t('aiUpsell.title')}</Typography>
        </Stack>
        <AiUpsellContent featureLabel={featureLabel} variant="panel" />
      </Paper>
    </Box>
  );
}

/** Mounted once around the routes, so every screen (including dialogs and the patient workspace) can open it. */
export function AiUpsellProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [featureLabel, setFeatureLabel] = useState<string | undefined>(undefined);

  const openUpsell = useCallback((label?: string) => {
    setFeatureLabel(label);
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ openUpsell }), [openUpsell]);
  const close = () => setOpen(false);

  return (
    <AiUpsellContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby="ai-upsell-title">
        <DialogTitle id="ai-upsell-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <Box component="span" sx={{ color: 'primary.main', display: 'flex' }}><Sparkles size={20} /></Box>
          {t('aiUpsell.title')}
        </DialogTitle>
        <DialogContent>
          {open && <AiUpsellContent featureLabel={featureLabel} onClose={close} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('aiUpsell.close')}</Button>
        </DialogActions>
      </Dialog>
    </AiUpsellContext.Provider>
  );
}
